import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import CONFIG from "../../config/config";
import SignUpForm from "../../components/SignUpForm/SignUpForm";
import OnboardingFlow from "../../components/OnboardingFlow/OnboardingFlow";
import Title from "../../components/Title/Title";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import ReferralsAPI from "../../api/ReferralsAPI";

type Stage = "signup" | "flow";

const hasSessionToken = () => {
  if (typeof window === "undefined") return false;
  return !!sessionStorage.getItem(CONFIG.LOCAL_SESSION.TOKEN);
};

const OnboardingEntryPage = () => {
  const { t } = useTranslation();
  const settings = useSelector((state: any) => state.settings.value);
  const [searchParams] = useSearchParams();

  const referralCode = useMemo(() => {
    return searchParams.get("refCode") || null;
  }, [searchParams]);

  const initialStage: Stage = useMemo(() => {
    return settings?.isLoggedIn || hasSessionToken() ? "flow" : "signup";
  }, [settings?.isLoggedIn]);

  const [stage, setStage] = useState<Stage>(initialStage);

  const handleSignupSuccess = async () => {
    if (referralCode) {
      try {
        await ReferralsAPI.record(referralCode);
        console.log("Referral recorded successfully:", referralCode);
      } catch (error) {
        console.error("Failed to record referral:", error);
      }
    }
    setStage("flow");
  };

  if (stage === "signup") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-darker-gold-50 via-white to-as-pink-100">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-darker-gold-200 rounded-full opacity-10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-as-pink-300 rounded-full opacity-10 blur-3xl" />
        </div>
        <div className="relative z-10 w-full max-w-2xl bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border-0 p-10 space-y-6">
          <div className="text-center space-y-3">
            <Title
              text={t("onboarding.signup.title") as string}
              clazz="text-3xl font-bold text-darker-gold-default text-center !text-center"
            />
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto text-center">
              {t("onboarding.signup.subtitle") ||
                "Create your account to continue with onboarding."}
            </p>
            <div className="h-1 w-32 bg-gradient-to-r from-darker-gold-300 to-as-pink-300 rounded-full mx-auto" />
          </div>
          <SignUpForm callbackFn={handleSignupSuccess} />
          <LanguageSwitcher className="flex justify-center mt-2" />
          <p className="text-center text-sm text-gray-500">
            {t("onboarding.signup.already_have_account") ||
              "Already have an account?"}{" "}
            <Link to="/login" className="text-as-pink-300 font-semibold">
              {t("registration.login")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return <OnboardingFlow />;
};

export default OnboardingEntryPage;

